import type { MouseEvent, FunctionComponent, ReactNode } from 'react';
import classnames from 'classnames';
import Flex from '../Flex';
import Icon from '../Icon';
import type { IconClassName } from '../Icon';
import type { ButtonType } from '../Button';
import Button, { BUTTON_TYPE, BUTTON_SIZE } from '../Button';
import styles from './styles.module.less';

type CardBannerProps = {
  /**
   * Determines the theme of the CardBanner.
   */
  variant?: CardBannerVariant;
  /**
   * Title for the CardBanner
   */
  title?: string;
  /**
   * Description for the CardBanner
   */
  description?: ReactNode;
  /**
   * Icon to be displayed beside the title.
   */
  icon?: IconClassName;
  /**
   * Call To Action
   */
  cta?: {
    text: string,
    onClick: (event: MouseEvent<HTMLElement>) => void,
    isFullWidth?: boolean;
  },
  /**
   * Determines whether to stack the header & cta vertically
   */
  stackHeaderCta?: boolean;
};

export enum CardBannerVariant {
  SUCCESS = 'success',
  INFO = 'info',
  DANGER = 'danger',
}

const mapButtonType = new Map<CardBannerVariant, ButtonType>([
  [CardBannerVariant.SUCCESS, BUTTON_TYPE.PRIMARY],
  [CardBannerVariant.INFO, BUTTON_TYPE.SECONDARY],
  [CardBannerVariant.DANGER, BUTTON_TYPE.ERROR],
]);

/**
 * A CardBanner component that blends card with banner styling, featuring a bordered design and lighter background with optional CTA.
 * Supports different visual variants (defaults to "success").
 * All props are optional!
 *
 * @example simple
 * <CardBanner title="MyTitle" />
 * @example with ReactNode
 * <CardBanner title="MyTitle"><>MyChildren</></CardBanner>
 */
const CardBanner: FunctionComponent<CardBannerProps> = ({
  title,
  description,
  icon,
  cta,
  variant = CardBannerVariant.SUCCESS,
  stackHeaderCta = true,
  children,
}) => {
  const cardBannerClass = classnames(styles.cardBanner, {
    [styles.cardBannerSuccess]: variant === CardBannerVariant.SUCCESS,
    [styles.cardBannerInfo]: variant === CardBannerVariant.INFO,
    [styles.cardBannerDanger]: variant === CardBannerVariant.DANGER,
  });
  const buttonType = mapButtonType.get(variant);
  return (
    <Flex
      direction="column"
      align="flex-start"
      className={cardBannerClass}
    >
      <Flex
        direction={stackHeaderCta ? 'column' : 'row'}
        align="flex-start"
        justify="space-between"
        gap={24}
        className={styles.cardBannerContainer}
      >
        <Flex direction="column" align="flex-start" gap={8} grow={3}>
          <Flex align="flex-start" className={styles.cardBannerHeader}>
            {icon && <Icon icon={icon} />}
            {title && <div>{title}</div>}
          </Flex>
          {description && <div className={styles.cardBannerDescription}>{description}</div>}
        </Flex>
        {cta &&
          <Button
            type={buttonType}
            size={BUTTON_SIZE.MEDIUM}
            onClick={cta.onClick}
            isFullWidth={cta?.isFullWidth}
          >{cta.text}
          </Button>}
      </Flex>

      {children &&
        <>
          <hr className={styles.cardBannerDivider} />
          {children}
        </>}
    </Flex>
  );
};

export default CardBanner;
